import { Option } from '@blisspointmedia/bpm-types/dist/FilterBar';
import { Dispatch, SetStateAction, useCallback, useMemo, useState } from 'react';
import { useLatestRef } from '@src/utils/hooks/useLatestRef';

export const BASIC_FILTER_STORAGE_KEY = 'basic_filter_';
export const ADVANCED_FILTER_STORAGE_KEY = 'advanced_filter_';

interface UseFilterBarTokensParams {
  defaultTokens: Option[];
  disableAutoFilter?: boolean;
  initialValue?: string;
  onChange: (newTokens: Option[]) => void;
  // Called from setTokens when !disableAutoFilter. Kept in a ref internally so
  // setTokens stays stable even as the filter function closes over changing state.
  onAutoFilter: (tokens: Option[]) => void;
  storageKey: string;
  // Disambiguates the sessionStorage key when a page renders more than one filterable table at the
  // same pathname (the key is otherwise pathname-only) -- without it, two tables on one page share
  // one saved-filter slot and silently clobber/misapply each other's tokens on mount.
  storageKeySuffix?: string;
  tokens?: Option[];
  useLocalStorage: boolean;
}

interface UseFilterBarTokensResult {
  input: string;
  ourTokens: Option[];
  setInput: Dispatch<SetStateAction<string>>;
  setTokens: (newTokens: Option[]) => void;
}

export const useFilterBarTokens = ({
  defaultTokens,
  disableAutoFilter,
  initialValue,
  onChange,
  onAutoFilter,
  storageKey,
  storageKeySuffix,
  tokens,
  useLocalStorage
}: UseFilterBarTokensParams): UseFilterBarTokensResult => {
  const fullStorageKey = `${storageKey}${window.location.pathname}${storageKeySuffix ? `:${storageKeySuffix}` : ''}`;

  const localToken = useMemo(() => {
    if (!useLocalStorage) return '';
    return window.sessionStorage.getItem(fullStorageKey) ?? '';
  }, [useLocalStorage, fullStorageKey]);

  const savedTokens = initialValue || localToken;
  const parsedSavedTokens = useMemo<Option[]>(() => {
    try {
      return JSON.parse(savedTokens);
    } catch {
      return [];
    }
  }, [savedTokens]);

  const [input, setInput] = useState('');
  const [localTokens, setLocalTokens] = useState(
    parsedSavedTokens.length > 0 ? parsedSavedTokens : defaultTokens
  );

  const ourTokens = tokens ?? localTokens;

  const onAutoFilterRef = useLatestRef(onAutoFilter);

  const setTokens = useCallback(
    (newTokens: Option[]) => {
      setLocalTokens(newTokens);
      onChange(newTokens);
      if (!disableAutoFilter) {
        onAutoFilterRef.current(newTokens);
      }
      if (!useLocalStorage) return;
      window.sessionStorage.setItem(fullStorageKey, JSON.stringify(newTokens));
    },
    [onChange, disableAutoFilter, onAutoFilterRef, useLocalStorage, fullStorageKey]
  );

  return { input, setInput, ourTokens, setTokens };
};
